import { useAuthStore, reachableBranches } from './chunk-G2RM2HWO.js';
export { authEvents, coversBranch, formatOrgScope, grantForRole, isBranchPinned, parseOrgScope, reachableBranches, soleBranch, unrestrictedGrant, useAuthStore } from './chunk-G2RM2HWO.js';
import { useEffect } from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';

// src/hooks/useAuth.ts
function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  return { user, isAuthenticated, isLoading };
}

// src/permissions/rolePermissions.ts
var ADMIN_FULL = [
  "kyc:view",
  "kyc:approve",
  "ledger:view",
  "ledger:manage",
  "pos:operate",
  "loans:manage",
  "partner:manage",
  "compliance:review",
  "admin:manage-tenants",
  "admin:manage-users",
  "reports:view"
];
var RM_PERMS = ["kyc:view", "kyc:approve", "ledger:view", "compliance:review", "reports:view"];
var FINANCING_OFFICER_PERMS = ["loans:manage", "kyc:view", "reports:view"];
var ROLE_PERMISSIONS = {
  PLATFORM_ADMIN: ADMIN_FULL,
  TENANT_ADMIN: [
    "kyc:view",
    "kyc:approve",
    "ledger:view",
    "ledger:manage",
    "pos:operate",
    "loans:manage",
    "partner:manage",
    "compliance:review",
    "admin:manage-users",
    "reports:view"
  ],
  // A tenant's marketer. Campaigns, offers and coupons are the job, and none of them has a
  // coarse `Permission` here — this catalogue predates campaign- and commerce-service and gates
  // only the older consoles. `reports:view` is what it genuinely holds; the campaign and commerce
  // screens gate on the backend's own three-part tokens, which is the authority regardless.
  MARKETING: ["reports:view"],
  RELATIONSHIP_MANAGER: RM_PERMS,
  COMPLIANCE_OFFICER: ["kyc:view", "kyc:approve", "compliance:review", "reports:view"],
  // kyc:approve mirrors the backend's D4 grant (customer-onboarding-kyc-design.md):
  // manual OPERATIONS review is the KYC decision path of record.
  OPERATIONS: ["kyc:view", "kyc:approve", "ledger:view", "reports:view"],
  JEWELLER: ["pos:operate", "ledger:view", "reports:view"],
  AGENT: ["loans:manage", "kyc:view", "reports:view"],
  FINANCING_OFFICER: FINANCING_OFFICER_PERMS,
  SAVER: [],
  // Pulse and Helpdesk gate on the backend's own three-part tokens; neither has a
  // coarse `Permission` in this pre-campaign-era catalogue, same as MARKETING's note.
  SRE: [],
  SUPPORT: [],
  // The support desk's lead gates on helpdesk-service's own three-part tokens, same as SUPPORT.
  SUPPORT_LEAD: [],
  // The Ledger console gates on the backend's own three-part tokens, same as SRE/SUPPORT.
  FINANCE: [],
  // A network operator's administrator. Like MARKETING/SRE/SUPPORT/FINANCE it holds nothing in
  // this pre-campaign-era coarse catalogue — the Network console gates on the backend's own
  // three-part tokens. Present because `Record<Role, Permission[]>` is exhaustive, and the entry
  // was missed when NETWORK_ADMIN was added to `Role`: the omission had this package's typecheck
  // red on main.
  NETWORK_ADMIN: [],
  // QuickBee Marketing's own desk (marketing engine WP-10). Nothing in this coarse
  // pre-campaign-era catalogue: the managed-marketing console gates on the backend's own
  // three-part tokens (`network-managed-marketing:*`), like MARKETING/SRE/SUPPORT/FINANCE.
  // Present because `Record<Role, Permission[]>` is exhaustive — the omission when
  // NETWORK_ADMIN was added had this package's typecheck red on main.
  CAMPAIGN_MANAGER: []
};
function roleHasPermission(roles, permission) {
  return roles.some((role) => ROLE_PERMISSIONS[role]?.includes(permission));
}

// src/hooks/usePermission.ts
function usePermission(permission) {
  const roles = useAuthStore((s) => s.roles);
  return roleHasPermission(roles, permission);
}

// src/hooks/useCsrfToken.ts
function useCsrfToken() {
  return useAuthStore((s) => s.user?.csrfToken ?? null);
}

// src/hooks/useBranchScope.ts
function useBranchScope() {
  const scopes = useAuthStore((s) => s.user?.branchScopes);
  const activeBranch = useAuthStore((s) => s.activeBranch);
  const setActiveBranch = useAuthStore((s) => s.setActiveBranch);
  const reachable = reachableBranches(scopes);
  return {
    reachable,
    activeBranch,
    setActiveBranch,
    canSwitch: reachable !== null && reachable.length > 1,
    scopes
  };
}

// src/hooks/useDelegation.ts
function useDelegation() {
  const delegation = useAuthStore((s) => s.delegation);
  const begin = useAuthStore((s) => s.beginDelegation);
  const end = useAuthStore((s) => s.endDelegation);
  return { delegation, isDelegated: delegation !== null, begin, end };
}
function AuthGuard({
  children,
  fallback = null,
  requiredRoles,
  onUnauthenticated,
  onForbidden
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const roles = useAuthStore((s) => s.roles);
  const isForbidden = isAuthenticated && !!requiredRoles && requiredRoles.length > 0 && !requiredRoles.some((role) => roles.includes(role));
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      onUnauthenticated?.();
      return;
    }
    if (isForbidden) {
      onForbidden?.();
    }
  }, [isLoading, isAuthenticated, isForbidden]);
  if (isLoading) return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  if (!isAuthenticated) return null;
  if (isForbidden) return null;
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function PermissionGuard({ permission, children, fallback = null }) {
  const allowed = usePermission(permission);
  if (!allowed) return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/passwordPolicy.ts
var PASSWORD_POLICY = Object.freeze({
  minLength: 12,
  requireLowercase: true,
  requireUppercase: true,
  requireDigit: true,
  requireSymbol: false
});
function passwordRules(policy = PASSWORD_POLICY) {
  const rules = [
    {
      id: "length",
      label: `At least ${policy.minLength} characters`,
      test: (password) => password.length >= policy.minLength
    }
  ];
  if (policy.requireLowercase) {
    rules.push({ id: "lowercase", label: "A lower-case letter", test: (p) => /[a-z]/.test(p) });
  }
  if (policy.requireUppercase) {
    rules.push({ id: "uppercase", label: "An upper-case letter", test: (p) => /[A-Z]/.test(p) });
  }
  if (policy.requireDigit) {
    rules.push({ id: "digit", label: "A number", test: (p) => /\d/.test(p) });
  }
  if (policy.requireSymbol) {
    rules.push({ id: "symbol", label: "A symbol", test: (p) => /[^A-Za-z0-9]/.test(p) });
  }
  return rules;
}
function evaluatePassword(password, policy = PASSWORD_POLICY) {
  const rules = passwordRules(policy).map((rule) => ({
    id: rule.id,
    label: rule.label,
    met: rule.test(password)
  }));
  return { rules, satisfied: rules.every((rule) => rule.met) };
}
function describePasswordPolicy(policy = PASSWORD_POLICY) {
  const parts = [];
  if (policy.requireLowercase && policy.requireUppercase) parts.push("upper and lower case letters");
  else if (policy.requireLowercase) parts.push("a lower-case letter");
  else if (policy.requireUppercase) parts.push("an upper-case letter");
  if (policy.requireDigit) parts.push("a number");
  if (policy.requireSymbol) parts.push("a symbol");
  const classes = parts.length === 0 ? "" : ` with ${joinNaturally(parts)}`;
  return `Use at least ${policy.minLength} characters${classes}.`;
}
function joinNaturally(parts) {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

// src/redirect.ts
var LOCALE_PREFIX = /^\/[a-z]{2}(?:-[A-Z]{2})?(?=[/?#]|$)/;
var SELF_RESOLVING = /^\/[a-z]{2}(?:-[A-Z]{2})?(?:\/(?:login|register|website|apps|403)(?=[/?#]|$)|\/?(?=[?#]|$))/;
var UNSAFE_CHARS = /[\s\x00-\x1f\x7f]/;
function safeRedirectPath(raw, { locale }) {
  if (typeof raw !== "string") return void 0;
  const value = raw.trim();
  if (!value.startsWith("/")) return void 0;
  if (/^\/[\\/]/.test(value)) return void 0;
  if (value.includes("\\")) return void 0;
  if (UNSAFE_CHARS.test(value)) return void 0;
  try {
    const resolved = new URL(value, "https://placeholder.invalid");
    if (resolved.origin !== "https://placeholder.invalid") return void 0;
  } catch {
    return void 0;
  }
  const path = LOCALE_PREFIX.test(value) ? value : `/${locale}${value}`;
  if (SELF_RESOLVING.test(path)) return void 0;
  return path;
}

export { AuthGuard, PASSWORD_POLICY, PermissionGuard, describePasswordPolicy, evaluatePassword, passwordRules, safeRedirectPath, useAuth, useBranchScope, useCsrfToken, useDelegation, usePermission };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map